import { useEffect, useState, useRef, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { chainIdAtom } from '@/state/atoms'
import { ethPriceAtom } from '@/state/chain/atoms/chainAtoms'
import { Token } from '@/types'
import { AuctionMetrics } from '@reserve-protocol/dtf-rebalance-lib'
import { TokenLiquidity } from '@/utils/liquidity'
import {
  fetchRebalanceLiquidity,
  toTokenLiquidity,
  LiquidityTrade,
  OndoInfo,
  OndoMarket,
} from '@/utils/rebalance-liquidity'
export { NATIVE_SYMBOL } from '@/utils/zapper'
import { currentRebalanceAtom } from '../../../atoms'
import { rebalanceMetricsAtom, rebalanceTokenMapAtom } from '../atoms'
import { isRebalanceOngoing } from '../utils'
import useRebalanceParams from './use-rebalance-params'

const DEBOUNCE_DELAY = 1_000

export type TokenInfo = {
  tokenAddress: string
  tokenSymbol: string
  usdSize: number
  type: 'surplus' | 'deficit'
}

type LiquidityData = {
  liquidityMap: Record<string, TokenLiquidity>
  ondoMap: Record<string, OndoInfo>
  market: OndoMarket | null
}

const EMPTY: LiquidityData = { liquidityMap: {}, ondoMap: {}, market: null }

const getAllTokensWithSizes = (
  metrics: AuctionMetrics | undefined,
  tokenMap: Record<string, Token>
): TokenInfo[] => {
  if (!metrics) return []

  const surplus: TokenInfo[] = []
  const deficit: TokenInfo[] = []

  for (let i = 0; i < metrics.surplusTokens.length; i++) {
    const tokenAddress = metrics.surplusTokens[i].toLowerCase()
    const token = tokenMap[tokenAddress]
    if (token && metrics.surplusTokenSizes[i] > 0) {
      surplus.push({
        tokenAddress,
        tokenSymbol: token.symbol,
        usdSize: metrics.surplusTokenSizes[i],
        type: 'surplus',
      })
    }
  }

  for (let i = 0; i < metrics.deficitTokens.length; i++) {
    const tokenAddress = metrics.deficitTokens[i].toLowerCase()
    const token = tokenMap[tokenAddress]
    if (token && metrics.deficitTokenSizes[i] > 0) {
      deficit.push({
        tokenAddress,
        tokenSymbol: token.symbol,
        usdSize: metrics.deficitTokenSizes[i],
        type: 'deficit',
      })
    }
  }

  // An auction only trades min(surplus, deficit) — the larger side reports the
  // full available amount, not what's actually sold/bought. Scale each side to
  // the matched auction size so the checker reflects the real trade (and tracks
  // the rebalance percent on both sides, not just the deficit).
  const surplusTotal = surplus.reduce((s, t) => s + t.usdSize, 0)
  const deficitTotal = deficit.reduce((s, t) => s + t.usdSize, 0)
  const auctionSize = Math.min(surplusTotal, deficitTotal)
  const sScale = surplusTotal > 0 ? auctionSize / surplusTotal : 0
  const dScale = deficitTotal > 0 ? auctionSize / deficitTotal : 0

  return [
    ...surplus.map((t) => ({ ...t, usdSize: t.usdSize * sScale })),
    ...deficit.map((t) => ({ ...t, usdSize: t.usdSize * dScale })),
  ]
}

const buildResult = (
  assets: Awaited<ReturnType<typeof fetchRebalanceLiquidity>>['assets'],
  market: OndoMarket | null
): LiquidityData => {
  const liquidityMap: Record<string, TokenLiquidity> = {}
  const ondoMap: Record<string, OndoInfo> = {}
  for (const asset of assets) {
    liquidityMap[asset.address] = toTokenLiquidity(asset)
    if (asset.ondo) ondoMap[asset.address] = asset.ondo
  }
  return { liquidityMap, ondoMap, market }
}

const useRebalanceLiquidityCheck = () => {
  const metrics = useAtomValue(rebalanceMetricsAtom)
  const tokenMap = useAtomValue(rebalanceTokenMapAtom)
  const chainId = useAtomValue(chainIdAtom)
  const ethPrice = useAtomValue(ethPriceAtom)
  const rebalance = useAtomValue(currentRebalanceAtom)
  const rebalanceParams = useRebalanceParams()
  const queryClient = useQueryClient()

  const [debouncedMetrics, setDebouncedMetrics] = useState(metrics)
  const [retryingTokens, setRetryingTokens] = useState<Set<string>>(new Set())
  const debounceTimerRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedMetrics(metrics)
    }, DEBOUNCE_DELAY)
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [metrics])

  const tokens = useMemo(
    () => getAllTokensWithSizes(debouncedMetrics, tokenMap),
    [debouncedMetrics, tokenMap]
  )

  const buildTrades = (tokenList: TokenInfo[]): LiquidityTrade[] =>
    tokenList.map((t) => ({
      address: t.tokenAddress,
      side: t.type === 'surplus' ? 'sell' : 'buy',
      amountUsd: t.usdSize,
      price: rebalanceParams?.prices[t.tokenAddress]?.currentPrice ?? 0,
      decimals: tokenMap[t.tokenAddress]?.decimals ?? 18,
    }))

  const queryKey = [
    'rebalance-liquidity',
    tokens,
    chainId,
    rebalanceParams?.prices,
    ethPrice,
  ]

  const {
    data = EMPTY,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<LiquidityData>({
    queryKey,
    queryFn: async () => {
      if (!tokens.length || !rebalanceParams || !chainId) return EMPTY
      try {
        const res = await fetchRebalanceLiquidity(
          chainId,
          ethPrice,
          buildTrades(tokens)
        )
        return buildResult(res.assets, res.market)
      } catch (e) {
        console.error('Failed to fetch rebalance liquidity', e)
        return EMPTY
      }
    },
    enabled: !!tokens.length && !!rebalanceParams && !!chainId && !!ethPrice,
    staleTime: 30_000,
    // Only poll while the rebalance window is open; stop on completed/historical.
    refetchInterval: () =>
      isRebalanceOngoing(
        rebalance?.rebalance.availableUntil,
        Math.floor(Date.now() / 1000)
      )
        ? 30_000
        : false,
  })

  const { liquidityMap, ondoMap, market } = data

  const retryToken = async (tokenAddress: string) => {
    const token = tokens.find((t) => t.tokenAddress === tokenAddress)
    if (!token || !rebalanceParams || !chainId || !ethPrice) return

    setRetryingTokens((prev) => new Set(prev).add(tokenAddress))
    try {
      const res = await fetchRebalanceLiquidity(
        chainId,
        ethPrice,
        buildTrades([token])
      )
      const asset = res.assets[0]
      if (!asset) return
      queryClient.setQueryData<LiquidityData>(queryKey, (old) => ({
        liquidityMap: {
          ...old?.liquidityMap,
          [tokenAddress]: toTokenLiquidity(asset),
        },
        ondoMap: asset.ondo
          ? { ...old?.ondoMap, [tokenAddress]: asset.ondo }
          : old?.ondoMap ?? {},
        market: res.market ?? old?.market ?? null,
      }))
    } finally {
      setRetryingTokens((prev) => {
        const next = new Set(prev)
        next.delete(tokenAddress)
        return next
      })
    }
  }

  return {
    tokens,
    liquidityMap,
    ondoMap,
    market,
    isLoading,
    isFetching,
    retryingTokens,
    refetch,
    retryToken,
  }
}

export default useRebalanceLiquidityCheck
